import React from 'react'
import { FormReadonlyField, FormSelectField } from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LeverageInstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { UpdateLeverageInstrument } from 'api/Trading-Accounts/Types/UpdateLeverageInstrument'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  leverage?: LeverageInstrumentDto
  leverages: LeverageInstrumentDto[]

  onSubmit(values: FormSubmitValues<UpdateLeverageInstrument>): Promise<void>
}

const LeverageEditFormUI: React.FC<FormikProps<UpdateLeverageInstrument> & OuterProps> = (
  props
) => {
  const {
    leverage,
    leverages,
    handleSubmit,
    isSubmitting,
    touched,
    errors,
    setFieldValue,
    isValid,
  } = props

  const handleSubmitForm = () => {
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Leverage</DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            <FormReadonlyField name='symbol' label='Symbol' value={leverage?.symbol ?? ''} />
            <FormReadonlyField name='name' label='Name' value={leverage?.name ?? ''} />
            <FormReadonlyField name='type.name' label='Type' value={leverage?.type?.name ?? ''} />
            <FormSelectField
              name='leverageId'
              label='Leverage'
              options={(leverages || []).map((item) => ({
                id: item.id,
                name: item.name ?? '',
              }))}
              error={touched.leverageId ? errors.leverageId : undefined}
              onChange={(value) => {
                setFieldValue('leverageId', value ? Number(value) : undefined)
              }}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={handleSubmitForm} disabled={isSubmitting || !isValid} type='button'>
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

export const LeverageEditForm = withFormik<OuterProps, UpdateLeverageInstrument>({
  mapPropsToValues: ({ leverage }) => {
    const currentLeverageInstrument: Partial<LeverageInstrumentDto> = leverage || {}

    return {
      id: currentLeverageInstrument.id?.toString(),
      name: currentLeverageInstrument?.name || '',
      type: currentLeverageInstrument?.type,
      symbol: currentLeverageInstrument?.symbol || '',
      leverageId: currentLeverageInstrument?.leverageType?.id,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<UpdateLeverageInstrument> = {}

    if (!values.leverageId) {
      errors.leverageId = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
})(LeverageEditFormUI)
